import { useBaseConfirm } from '@/components/base/BaseConfirm';
import { readThemePalette } from '@/constants/theme';
import { useAppStore } from '@/store/app';
import { bindFeedback } from '@/utils/feedback';
import { StyleProvider } from '@ant-design/cssinjs';
import { App, ConfigProvider, theme as antdTheme } from 'antd';
import { useEffect, useMemo, type PropsWithChildren } from 'react';

/** 把 antd App 的 message / modal / notification 实例交给 utils/feedback 统一分发 */
function FeedbackBridge() {
  const app = App.useApp();
  const [confirm, holder] = useBaseConfirm();
  const feedback = useMemo(
    () => ({ ...app, modal: { ...app.modal, confirm } }),
    [app, confirm],
  );
  useEffect(() => {
    return bindFeedback(feedback);
  }, [feedback]);
  return holder;
}

/**
 * 主题 Provider
 *
 * 颜色的唯一来源是 `src/global.scss` 的 `:root`（`--muou-*`）。antd 的颜色 token 必须是
 * 可解析的具体值——它要拿这些值现算 hover / 浅底 / 禁用态，传 `var()` 会在启动时抛错白屏，
 * 所以这里先用 `readThemePalette()` 把变量读成具体值，再交给 antd。
 */
export default function AppProvider({ children }: PropsWithChildren) {
  const dark = useAppStore((state) => state.dark);

  const themeConfig = useMemo(() => {
    const palette = readThemePalette();
    return {
      algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: palette.primary,
        colorPrimaryHover: palette.primaryHover,
        colorPrimaryActive: palette.primaryActive,
        colorPrimaryBg: palette.primarySoft,
        colorPrimaryBorder: palette.primaryBorder,
        colorBgLayout: palette.bgLayout,
        colorBgContainer: palette.surface,
        colorBorder: palette.border,
        colorBorderSecondary: palette.split,
        colorText: palette.textBody,
        colorTextHeading: palette.text,
        colorTextSecondary: palette.textSecondary,
        colorTextTertiary: palette.textMuted,
        borderRadius: 6,
        controlHeight: 36,
        fontSize: 14,
        lineHeight: 1.65,
        // 原生组件和普通内容统一继承验收稿字体栈，仅使用系统已安装字体。
        fontFamily: 'Inter, "PingFang SC", "Microsoft YaHei", sans-serif',
      },
      components: {
        Layout: {
          headerHeight: 72,
          headerPadding: '0 24px',
          siderBg: palette.surface,
          headerBg: palette.surface,
        },
        Menu: {
          fontSize: 13,
          // 菜单选中背景用调色板里的半透明档，替代原先 JS 里的 withAlpha()
          itemSelectedBg: palette.primarySoft,
          itemHoverBg: palette.primarySoft,
          // 按下态与 hover 取同一个值：itemActiveBg 不给的话会落到 antd 默认的
          // controlItemBgActive，在这套主题色下派生出 #a8b3b2 这种不透明灰绿，
          // 点击瞬间会从几乎透明闪成一块深色再弹回来
          itemActiveBg: palette.primarySoft,
          // theme="dark" 的菜单走的是另一套 token，深色底上要加深才看得清
          darkItemSelectedBg: palette.primary,
          darkItemHoverBg: palette.primaryHover,
        },
        Button: {
          // 禁用色交给原生按钮 token，避免页面强制主色覆盖禁用与悬停状态。
          colorBgContainerDisabled: palette.buttonDisabledBg,
          colorTextDisabled: palette.buttonDisabledText,
          colorBorderDisabled: palette.border,
          fontWeight: 500,
          controlHeight: 36,
          controlHeightSM: 28,
          borderRadius: 6,
          borderRadiusSM: 6,
          paddingInlineSM: 8,
          contentFontSize: 13,
          contentFontSizeSM: 12,
          contentFontSizeLG: 14,
          primaryShadow: 'none',
        },
        // 聚焦色用主色本身：antd 默认拿 colorPrimaryHover（更深的 #1d4ed8）当
        // 输入框的 hover / 聚焦边框色，验收稿要求 focus 是主色 #2563EB。
        Input: {
          fontSize: 13,
          inputFontSizeLG: 14,
          activeBorderColor: palette.primary,
          hoverBorderColor: palette.primary,
          activeShadow: `0 0 0 2px ${palette.primarySoft}`,
        },
        InputNumber: {
          fontSize: 13,
          inputFontSizeLG: 14,
          activeBorderColor: palette.primary,
          hoverBorderColor: palette.primary,
        },
        Select: {
          fontSize: 13,
          fontSizeLG: 14,
          activeBorderColor: palette.primary,
          hoverBorderColor: palette.primary,
        },
        DatePicker: {
          fontSize: 13,
          inputFontSizeLG: 14,
          activeBorderColor: palette.primary,
          hoverBorderColor: palette.primary,
        },
        Card: {
          borderRadiusLG: 8,
          boxShadowTertiary: 'none',
          headerFontSize: 15,
          headerFontSizeSM: 13,
        },
        Table: {
          fontSize: 13,
          headerBg: palette.tableHead,
          headerColor: palette.textMuted,
          rowHoverBg: palette.rowHover,
          cellPaddingBlock: 10,
          cellPaddingInline: 12,
        },
        Pagination: {
          fontSize: 13,
          itemSize: 28,
          itemActiveBg: palette.primary,
          itemActiveColor: palette.surface,
        },
        // 正文采用14px；控件与字段值保持13px，避免根字号连带放大操作区。
        Form: { fontSize: 13 },
        Descriptions: { fontSize: 13, titleColor: palette.text },
        Radio: { fontSize: 13 },
        Checkbox: { fontSize: 13 },
        Segmented: { fontSize: 13 },
        TreeSelect: { fontSize: 13 },
        Tree: { fontSize: 13 },
        Dropdown: { fontSize: 13 },
        Tabs: { fontSize: 13 },
        Statistic: { contentFontSize: 22 },
      },
    };
  }, [dark]);

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      dark ? 'dark' : 'light',
    );
  }, [dark]);

  return (
    // layer：把 antd 样式放进 @layer antd，Tailwind utilities 才能覆盖它
    // （layer 顺序在 tailwind.css 里声明：antd 在最前，优先级最低）
    <StyleProvider layer>
      <ConfigProvider theme={themeConfig}>
        <App
          className="h-full"
          message={{
            classNames: { root: 'base-message', title: 'base-message-text' },
          }}
          notification={{
            classNames: {
              root: 'base-notification',
              description: 'base-notification-description',
            },
          }}
        >
          <FeedbackBridge />
          {children}
        </App>
      </ConfigProvider>
    </StyleProvider>
  );
}
